<template>
    <div class="block-editor cache-write">
        <div class="wrapper">
            <!-- Cache Key -->
            <section>
                <div class="title">{{ $t("tables.automationblock.cacheKey") }}</div>
                <TiptapEditor
                    :placeholder="$t('tables.automationblock.writeKeyPlaceholder')"
                    :show-attributes="true"
                    v-model="clone.key"
                    automation-mode
                />
                <div class="help-text">
                    <i class="material-icons">info</i>
                    <span>{{ $t("tables.automationblock.writeKeyHelp") }}</span>
                </div>
            </section>

            <!-- Cache Value -->
            <section>
                <div class="title">{{ $t("tables.automationblock.cacheValueTitle") }}</div>
                <TiptapEditor
                    :placeholder="$t('tables.automationblock.valueToSavePlaceholder')"
                    :show-attributes="true"
                    v-model="clone.value"
                    automation-mode
                />
            </section>

            <!-- Cache Type -->
            <section>
                <div class="title">{{ $t("tables.automationblock.cacheTypeTitle") }}</div>
                <CustomPicker
                    v-model="clone.type"
                    :items="typeOptions"
                    idKey="value"
                    labelKey="label"
                    :placeholder="$t('tables.automationblock.selectCacheTypePlaceholder')"
                />
                <div class="help-text">
                    <i class="material-icons">info</i>
                    <span v-if="clone.type === 'public'">{{ $t("tables.automationblock.cacheTypePublicHelp") }}</span>
                    <span v-else>{{ $t("tables.automationblock.cacheTypePrivateHelp") }}</span>
                </div>
            </section>

            <!-- Data Type -->
            <section>
                <div class="title">{{ $t("tables.automationblock.dataTypeTitle") }}</div>
                <CustomPicker
                    v-model="clone.dataType"
                    :items="dataTypeOptions"
                    idKey="value"
                    labelKey="label"
                    :placeholder="$t('tables.automationblock.selectDataTypePlaceholder')"
                />
                <div class="help-text">
                    <i class="material-icons">info</i>
                    <span>{{ $t("tables.automationblock.dataTypeUiOnlyHelp") }}</span>
                </div>
            </section>

            <!-- Expires At -->
            <section>
                <div class="title">{{ $t("tables.automationblock.validityDurationTitle") }}</div>
                <div class="expires-config">
                    <CustomPicker
                        v-model="clone.expiresAt.mode"
                        :items="expiresModeOptions"
                        idKey="value"
                        labelKey="label"
                        :placeholder="$t('tables.automationblock.timeTypePlaceholder')"
                    />

                    <div v-if="clone.expiresAt.mode === 'duration'" class="duration-config">
                        <TiptapEditor
                            :placeholder="$t('tables.automationblock.durationValuePlaceholder')"
                            :show-attributes="true"
                            v-model="clone.expiresAt.duration.value"
                            automation-mode
                        />
                        <CustomPicker
                            v-model="clone.expiresAt.duration.unit"
                            :items="durationUnitOptions"
                            idKey="value"
                            labelKey="label"
                            :placeholder="$t('tables.automationblock.unitPlaceholder')"
                        />
                    </div>

                    <div v-if="clone.expiresAt.mode === 'timestamp'" class="timestamp-config">
                        <TiptapEditor
                            :placeholder="$t('tables.automationblock.isoDatePlaceholder')"
                            :show-attributes="true"
                            v-model="clone.expiresAt.timestamp.value"
                            automation-mode
                        />
                    </div>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
import _ from "lodash";
import TiptapEditor from "../../../../../Chatbot/pages/dashboard/common/TiptapEditor.vue";
import CustomPicker from "../../../../../../../components/pickers/CustomPicker.vue";

export default {
    name: "CacheWrite",
    components: {
        TiptapEditor,
        CustomPicker,
    },
    props: ["value"],
    data() {
        return {
            clone: _.cloneDeep(this.value || {
                key: "",
                value: "",
                type: "public",
                dataType: "mixed",
                expiresAt: {
                    mode: "never",
                    duration: {
                        value: "1",
                        unit: "hours",
                    },
                    timestamp: {
                        value: "",
                    },
                },
            }),
        };
    },
    computed: {
        typeOptions() {
            return [
                { value: "public", label: this.$t("tables.automationblock.cacheTypePublicOption") },
                { value: "private", label: this.$t("tables.automationblock.cacheTypePrivateOption") },
            ];
        },
        dataTypeOptions() {
            return [
                { value: "mixed", label: this.$t("tables.automationblock.dataTypeMixed") },
                { value: "string", label: this.$t("tables.automationblock.dataTypeString") },
                { value: "number", label: this.$t("tables.automationblock.dataTypeNumber") },
                { value: "boolean", label: this.$t("tables.automationblock.dataTypeBoolean") },
                { value: "date", label: this.$t("tables.automationblock.dataTypeDate") },
                { value: "json", label: this.$t("tables.automationblock.dataTypeJson") },
                { value: "array", label: this.$t("tables.automationblock.dataTypeArray") },
                { value: "objectid_user", label: this.$t("tables.automationblock.dataTypeObjectIdUser") },
            ];
        },
        expiresModeOptions() {
            return [
                { value: "never", label: this.$t("tables.automationblock.neverExpireLabel") },
                { value: "duration", label: this.$t("tables.automationblock.expiresModeDurationLabel") },
                { value: "timestamp", label: this.$t("tables.automationblock.expiresModeTimestampLabel") },
            ];
        },
        durationUnitOptions() {
            return [
                { value: "seconds", label: this.$t("tables.automationblock.unitSecond") },
                { value: "minutes", label: this.$t("tables.automationblock.unitMinute") },
                { value: "hours", label: this.$t("tables.automationblock.unitHour") },
                { value: "days", label: this.$t("tables.automationblock.unitDay") },
            ];
        },
    },
    watch: {
        clone: {
            deep: true,
            handler(newValue) {
                this.$emit("input", newValue);
            },
        },
    },
};
</script>

<style lang="scss">
.block-editor.cache-write {
    .wrapper {
        padding: 30px;

        section {
            margin-bottom: 20px;

            .title {
                margin-bottom: 10px;
                font-weight: 600;
                font-size: 16px;
                line-height: 20px;
            }

            .help-text {
                display: flex;
                align-items: flex-start;
                gap: 8px;
                margin-top: 8px;
                padding: 10px;
                background: #f0f8ff;
                border-radius: 6px;
                font-size: 13px;
                color: #666;

                .material-icons {
                    font-size: 18px;
                    color: #1890ff;
                }
            }

            .expires-config {
                display: flex;
                flex-direction: column;
                gap: 15px;

                .duration-config {
                    display: flex;
                    gap: 10px;
                    align-items: flex-start;

                    > :first-child {
                        flex: 2;
                    }

                    > :last-child {
                        flex: 1;
                    }
                }

                .timestamp-config {
                    display: flex;
                    flex-direction: column;
                }
            }
        }
    }
}
</style>

